import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import * as SelectionActions from 'actions/SelectionActions';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {ConnectedMetadataEditor} from 'metadata/MetadataEditor';
import {HistoryState} from 'notebook/ELNConstants';
import EntryNoteUpdater from 'notebook/entries/EntryNoteUpdater';
import {TemplateVersion} from 'notebook/entry-templates/TemplateConstants';
import {
  EditorPropertiesContext,
  EditorPropertiesContextInterface,
  NoteContext,
} from 'notebook/notes/core-ui/NoteContext';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {NoteView} from 'notebook/notes/tabs/NoteViews';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {ConnectedReviewTab} from 'notebook/reviews/ReviewView';
// @ts-expect-error
import HistoryManager from 'notebook/store/HistoryManager';
import isConfiguration from 'notebook/util/isConfiguration';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import SectionTabs from 'ui-library/SectionTabs';
import APIUtil from 'util/APIUtil';
import memoizeLastCall from 'util/memoizeLastCall';
import * as modals from 'util/Modals';
import * as WorkflowEntryTemplateActions from 'workflows/entry-templates/WorkflowEntryTemplateActions';
import WorkflowEntryTemplateStore from 'workflows/entry-templates/WorkflowEntryTemplateStore';
import * as WorkflowEntryTemplateUtil from 'workflows/entry-templates/WorkflowEntryTemplateUtil';
import {
  FILE_TYPES_WITH_MODAL_EDITOR,
  StageConfiguration,
  WORKFLOW_ENTRY_TEMPLATE_SHAPE,
} from 'workflows/WorkflowConstants';

enum ModalTab {
  NOTES_TAB = 'NOTES_TAB',
  METADATA_TAB = 'METADATA_TAB',
  REVIEW_TAB = 'REVIEW_TAB',
}

interface WorkflowEntryTemplateModalProps {
  entryTemplate: StageConfiguration;
  isReadonly: boolean;
  fileType: string;
  onClose: () => void;
}

interface EnhancedProps {
  templateVersionFromStore: TemplateVersion | null;
}

interface State {
  activeTab: ModalTab;
  historyManager: HistoryManager;
  name: string;
  note: EntryNote | null;
  templateVersion: TemplateVersion | null;
}

class WorkflowEntryTemplateModalConnector extends DataConnector<
  WorkflowEntryTemplateModalProps,
  EnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [WorkflowEntryTemplateStore];

  enhanceProps(): EnhancedProps {
    if (!this.workflowEntryTemplateStore().inStore(this.props.entryTemplate.id)) {
      return {templateVersionFromStore: null};
    }

    const {entryTemplate} = this.workflowEntryTemplateStore().getEditorState(this.props.entryTemplate.id);
    const templateVersion = entryTemplate ? entryTemplate.templateVersion : null;
    return {
      templateVersionFromStore: templateVersion,
    };
  }
}

function makeEditorPropertiesContext(fileId: string): EditorPropertiesContextInterface {
  return {showFormulas: false, fileId, isConfig: isConfiguration(fileId)};
}

// Modal for editing StageEntryTemplates
class WorkflowEntryTemplateModal extends React.PureComponent<
  WorkflowEntryTemplateModalProps & EnhancedProps,
  State
> {
  _isMounted: boolean = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    entryTemplate: WORKFLOW_ENTRY_TEMPLATE_SHAPE.isRequired,
    isReadonly: PropTypes.bool.isRequired,
    fileType: PropTypes.oneOf(FILE_TYPES_WITH_MODAL_EDITOR),
    onClose: PropTypes.func,
    // Provided by WorkflowEntryTemplateModalConnector
    templateVersionFromStore: PropTypes.object,
  };

  override state: State = {
    activeTab: ModalTab.NOTES_TAB,
    historyManager: new HistoryManager(),
    name: this._getInitialEntryTemplateName(),
    note: this._transformNoteFromStoreForState(),
    templateVersion: this.props.templateVersionFromStore,
  };

  override componentDidMount(): void {
    this._isMounted = true;
    if (this.state.templateVersion) {
      // Don't re-fetch the version if it is currently being edited
      return;
    }
    WorkflowEntryTemplateActions.fetchTemplateVersion(this.props.entryTemplate.id);
  }

  override componentWillUnmount(): void {
    WorkflowEntryTemplateActions.discardEntryTemplate(this.props.entryTemplate.id);
    this._isMounted = false;
  }

  override componentDidUpdate(prevProps: WorkflowEntryTemplateModalProps & EnhancedProps): void {
    if (this.props.entryTemplate.id !== prevProps.entryTemplate.id) {
      throw new Error("Can't change the template while editing");
    }
    if (this.state.templateVersion) {
      // If the store has changed, but we're locally editing templateVersion, don't refresh from the store.
      return;
    }
    if (!isEqual(this.props.templateVersionFromStore, prevProps.templateVersionFromStore)) {
      const templateVersion = this.props.templateVersionFromStore;
      // TODO(BNCH-14590): Any pending edits will get blown away from async resolutions from store.
      this.setState({
        templateVersion,
        note: this._getNote(templateVersion),
      });
    }
  }

  _getUpdater(): EntryNoteUpdater {
    return new EntryNoteUpdater(
      this.props.entryTemplate.id,
      () => this.state.note,
      (note: EntryNote) => {
        if (this._isMounted) {
          this._updateNote(note);
        }
      },
      {multidocTableOp: {[this.props.entryTemplate.id]: 'eval-document-unsupported'}}
    );
  }

  _transformNoteFromStoreForState(): EntryNote | null {
    const note = this._getNote(this.props.templateVersionFromStore);
    return note ? this._getUpdater().run(note, null) : null;
  }

  _getNote(templateVersion: TemplateVersion | null): EntryNote | null {
    return templateVersion ? templateVersion.entryVersion.note : null;
  }

  _getInitialNote(): EntryNote | null {
    return this._getNote(this.state.templateVersion);
  }

  _getIdFromCid = (cid: string): string | number | undefined =>
    WorkflowEntryTemplateStore.getIdFromCid(this.props.entryTemplate.id, cid);

  _hasUnsavedChanges(): boolean {
    return this.state.note !== this._getInitialNote();
  }

  _registerObjects = (cidIdMap: {[cid: string]: string}): void => {
    WorkflowEntryTemplateActions.registerObjects(this.props.entryTemplate.id, cidIdMap);
  };

  _restoreStateAndSelection = (
    {note, title}: HistoryState<EntryNote>,
    restoreData: SelectionRestoreData
  ): void => {
    SelectionActions.setSelection(restoreData);
    this.setState({note, name: title});
  };

  _updateNote = (note: EntryNote): void => {
    const oldNote = this.state.note;
    const sanitizedNote = NoteStoreTransforms.sanitizeNote(note, oldNote);
    const updatedNote = this._getUpdater().run(sanitizedNote, oldNote);
    this.setState({note: updatedNote});
  };

  _updateTitle = (name: string): void => {
    this.setState({name});
  };

  _handleClose = (): void => {
    const promise = !this._hasUnsavedChanges()
      ? new Promise<void>((resolve) => {
          resolve();
        })
      : modals.confirm(
          'Discard Changes?',
          'There are unsaved changes to your workflow template. Are you sure you would like to discard them?',
          'danger',
          {acceptText: 'Discard', rejectText: 'Cancel'}
        );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (promise.then as any)(this.props.onClose);
  };

  _handleSetActiveTab(key: ModalTab): void {
    this.setState({activeTab: key});
  }

  _handleSubmit = (): void => {
    const {note, name} = this.state;
    WorkflowEntryTemplateActions.updateAndSaveEntryTemplate(this.props.entryTemplate.id, note!, name, {
      onSuccess: this.props.onClose,
    });
  };

  _getButtons(): Array<ModalButton> {
    if (this.props.isReadonly) {
      return [
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Done',
          cancel: true,
        },
      ];
    }

    const saveButton = {
      text: 'Save',
      style: ButtonStyle.PRIMARY,
      submit: true,
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      disabled: this.state.templateVersion === null,
    };

    return [saveButton];
  }

  _getEntryTemplateName(entryTemplate: StageConfiguration): string {
    return entryTemplate[
      WorkflowEntryTemplateUtil.getServerNameField(entryTemplate.id) as 'entryTemplateName'
    ];
  }

  _getInitialEntryTemplateName(): string {
    return this._getEntryTemplateName(this.props.entryTemplate);
  }

  _renderNoteView(): JSX.Element {
    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
      <NoteContext.Provider value={{note: this.state.note ?? undefined}}>
        <NoteView
          fileId={this.props.entryTemplate.id}
          fileType={this.props.fileType}
          note={this.state.note}
          title={this.state.name}
          updateNote={this._updateNote}
          updateTitle={this._updateTitle}
          restoreStateAndSelection={this._restoreStateAndSelection}
          historyManager={this.state.historyManager}
          preview={{
            showOpenLink: true,
            followLink: true,
          }}
          disableFormatting={false}
          disableMaximizedTables={true}
          parentType="Workflow Entry Template"
          locked={this.props.isReadonly}
          banner={this.props.isReadonly ? 'Editing disabled on read-only template.' : null}
          printOptions={[]}
          showTimestamps={false}
          spellCheck={false}
          printLayout={true}
          showTableOfContents={false}
          showPrintMargins={false}
          registerObjects={this._registerObjects}
          getIdFromCid={this._getIdFromCid}
          permissions={this.props.entryTemplate.permissions}
        />
      </NoteContext.Provider>
    );
  }

  _renderMetadataView(): JSX.Element {
    const file = this.props.entryTemplate;
    const permissions = file.permissions;
    const fileTags = _.sortBy(file.tags, 'name');
    return (
      <ConnectedMetadataEditor
        bufferOptionsText={this.props.isReadonly ? 'Editing disabled on read-only template.' : null}
        fileId={this.props.entryTemplate.id}
        fileName={file.stageName}
        updateName={async () => {}}
        permissions={permissions}
        isRegistryWritable={permissions.writable}
        tags={fileTags}
        hasCustomTagSupport={true}
        hasFileSchemaSupport={true}
        hasRegistrySupport={false}
        tagSchema={file.tagSchema}
        shouldAllowChangeCollection={false}
        onAnalyzeProteins={() => {}}
      />
    );
  }

  _renderTemplate(): JSX.Element {
    const fileId = this.props.entryTemplate.id;
    const fileType = APIUtil.apiIdToType(fileId);

    const tabList = [ModalTab.NOTES_TAB];
    if (BenchlingConfig.ENABLE_ENTRY_SCHEMAS && TagSchemaUtil.hasFileSchemaSupport(fileId)) {
      tabList.push(ModalTab.METADATA_TAB);
    }
    if (BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA && ReviewUtil.canUserReviewTemplate(fileType)) {
      tabList.push(ModalTab.REVIEW_TAB);
    }

    if (tabList.length === 1) {
      // Don't show tab pane if the only option is notes
      return this._renderNoteView();
    }

    return (
      <div className="workflowEntryTemplateModal-tabContainer u-flex-grow">
        <SectionTabs
          tabs={tabList.map((tabValue) => ({
            handleClick: () => {
              this._handleSetActiveTab(tabValue);
            },
            ariaControls: `${tabValue}_PANEL`,
            id: tabValue,
            isActive: this.state.activeTab === tabValue,
            text: {
              [ModalTab.NOTES_TAB]: 'Notes',
              [ModalTab.METADATA_TAB]: 'Metadata',
              [ModalTab.REVIEW_TAB]: 'Review',
            }[tabValue],
          }))}
        />
        <div
          aria-labelledby={this.state.activeTab}
          className="workflowEntryTemplateModal-tabPane"
          id={`${this.state.activeTab}_PANEL`}
          role="tabpanel"
        >
          <EditorPropertiesContext.Provider value={memoizeLastCall(makeEditorPropertiesContext)(fileId)}>
            {(() => {
              switch (this.state.activeTab) {
                case ModalTab.NOTES_TAB:
                  return this._renderNoteView();
                case ModalTab.METADATA_TAB:
                  return this._renderMetadataView();
                case ModalTab.REVIEW_TAB:
                  return (
                    <ConnectedReviewTab fileId={fileId} permissions={{writable: !this.props.isReadonly}} />
                  );
                default:
                  throw new Error(`Invalid tab "${this.state.activeTab}"`);
              }
            })()}
          </EditorPropertiesContext.Provider>
        </div>
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <Modal
        className="modal--mediocre"
        fullWidth={true}
        title={this.props.isReadonly ? this._getInitialEntryTemplateName() : 'Edit Template'}
        onRequestClose={this._handleClose}
        onRequestSubmit={this.props.isReadonly ? null : this._handleSubmit}
        buttons={this._getButtons()}
        wrapInForm={false} // Avoid nesting forms (NoteToolbar renders a form)
        closeableOnEscapeKey={false}
      >
        {this.state.templateVersion != null ? (
          this._renderTemplate()
        ) : (
          <div className="text-center u-padding-top">
            <HorizontalThrobber />
          </div>
        )}
      </Modal>
    );
  }
}

export default WorkflowEntryTemplateModalConnector.connect(WorkflowEntryTemplateModal);
